import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { useUser } from '@/components/auth-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

import { GoogleIcon } from './google-icon';
import { getFirebaseMessage } from './helpers';
import { signInSchema, type SignInValues } from './schema';

interface Props {
  onForgotPassword: () => void;
  onSignUp: () => void;
}

export function SignInForm({ onForgotPassword, onSignUp }: Props) {
  const { logInWithEmail, logInWithGoogle } = useUser();
  const [googleLoading, setGoogleLoading] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({ resolver: zodResolver(signInSchema) });

  async function onSubmit({ email, password }: SignInValues) {
    const error = await logInWithEmail(email, password);
    if (error) toast.error(getFirebaseMessage(error));
  }

  async function handleGoogle() {
    setGoogleLoading(true);
    const error = await logInWithGoogle(getValues('email'));
    if (error) toast.error(getFirebaseMessage(error));
    setGoogleLoading(false);
  }

  return (
    <Card className="w-full max-w-sm gap-4 py-8">
      <CardHeader>
        <CardTitle className="text-center text-xl">Sign in</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
              data-testid="email-input"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-destructive text-xs">{errors.email.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-muted-foreground hover:text-foreground text-xs transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              aria-invalid={Boolean(errors.password)}
              data-testid="password-input"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-destructive text-xs">{errors.password.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="mt-1 w-full"
            data-loading={isSubmitting}
            data-testid="sign-in-button"
          >
            {isSubmitting && <Loader2Icon className="animate-spin" />}
            Sign in
          </Button>
        </form>

        <div className="flex items-center gap-2">
          <Separator className="flex-1" />
          <span className="text-muted-foreground text-xs">or</span>
          <Separator className="flex-1" />
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={handleGoogle}
          data-loading={googleLoading}
          data-testid="google-button"
          type="button"
        >
          {googleLoading ? (
            <Loader2Icon className="animate-spin" />
          ) : (
            <GoogleIcon />
          )}
          Continue with Google
        </Button>

        <p className="text-muted-foreground text-center text-sm">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            onClick={onSignUp}
            className="text-foreground font-medium underline-offset-4 hover:underline"
          >
            Sign up
          </button>
        </p>
      </CardContent>
    </Card>
  );
}
